import {
  Box,
  VStack,
  HStack,
  Text,
  Link as ChakraLink,
  Image,
  Flex,
  Menu,
  Portal,
} from '@chakra-ui/react';
import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { HiChevronRight, HiChevronDown, HiUser } from 'react-icons/hi2';

export interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  hasDividerAbove?: boolean;
  'data-e2e'?: string;
}

export interface UserMenuItem {
  label: string;
  icon?: React.ElementType;
  onClick: () => void;
  'data-e2e'?: string;
}

export interface CurrentUser {
  name: string;
  email?: string;
  avatar?: string;
}

interface SidebarProps {
  isOpen: boolean;
  onClose?: () => void;
  navItems: NavItem[];
  width?: string;
  logo?: React.ReactNode;
  currentUser?: CurrentUser;
  onUserMenuClick?: () => void;
  /** Menu items to show in the user dropdown */
  userMenuItems?: UserMenuItem[];
  /** Optional slot for additional actions (e.g., color mode toggle) */
  actions?: React.ReactNode;
  /** When true, the bottom-left user-menu block is not rendered. Use this when
   *  the user menu is hoisted into the page header (matches AIM's pattern). */
  hideUserMenu?: boolean;
  /** Optional bottom nav item (e.g., Settings) rendered above the user menu /
   *  actions slot, separated by a divider. Mirrors AIM's leftSideBar Settings
   *  link. */
  bottomNavItem?: NavItem;
}

export function Sidebar({
  isOpen,
  onClose,
  navItems,
  width = '220px',
  logo,
  currentUser,
  onUserMenuClick,
  userMenuItems,
  actions,
  hideUserMenu = false,
  bottomNavItem,
}: SidebarProps) {
  const location = useLocation();

  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (onClose) {
      onClose();
    }
  }, [location.pathname]);

  return (
    <>
      {/* Backdrop overlay for mobile */}
      {isOpen && (
        <Box
          position="fixed"
          inset={0}
          bg="surface.overlay"
          zIndex={1040}
          display={{ base: 'block', lg: 'none' }}
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <Box
        as="nav"
        position="fixed"
        left={0}
        top={0}
        bottom={0}
        w={{
          base: isOpen ? '280px' : 0,
          lg: isOpen ? width : 0,
        }}
        bg="surface.primary"
        borderRightWidth={isOpen ? '1px' : 0}
        borderColor="border.primary"
        overflow="hidden"
        transition="width 200ms cubic-bezier(0.4, 0, 0.2, 1)"
        zIndex={{ base: 1050, lg: 1020 }}
        boxShadow={{ base: isOpen ? 'lg' : 'none', lg: 'none' }}
        display="flex"
        flexDirection="column"
      >
        {/* Logo at top */}
        <Box>{logo}</Box>

        {/* Navigation items */}
        <VStack align="stretch" gap="10px" py="10px" flex={1} overflowY="auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <Box key={item.path}>
                {/* Divider above item if specified */}
                {item.hasDividerAbove && (
                  <Box
                    h="12px"
                    borderTopWidth="1px"
                    borderColor="#E5E5EA"
                    mb="10px"
                  />
                )}
                <ChakraLink
                  asChild
                  display="block"
                  w="full"
                  _hover={{ textDecoration: 'none' }}
                  _focusVisible={{
                    outline: '2px solid',
                    outlineColor: 'border.action',
                    outlineOffset: '2px',
                    borderRadius: '8px',
                  }}
                  data-e2e={item['data-e2e']}
                >
                  <Link
                    to={item.path}
                    style={{
                      textDecoration: 'none',
                      display: 'block',
                      width: '100%',
                    }}
                  >
                    {isActive ? (
                      /* Selected state */
                      <HStack gap="7px" align="center" pr={2} w="full">
                        <Box
                          w="3px"
                          h="23px"
                          bg="surface.action"
                          borderRadius="0 4px 4px 0"
                          flexShrink={0}
                        />
                        <HStack
                          flex={1}
                          w="full"
                          justify="space-between"
                          align="center"
                          gap={2}
                          p={2}
                          borderRadius="8px"
                          border="none"
                          bg="surface.menuSelect"
                          cursor="pointer"
                          transition="all 200ms"
                        >
                          <HStack gap={2}>
                            <Box color="text.action">
                              <Icon size={20} />
                            </Box>
                            <Text
                              fontSize="14px"
                              fontWeight="600"
                              color="text.primary"
                            >
                              {item.label}
                            </Text>
                          </HStack>
                          <Flex
                            p="2px"
                            align="center"
                            justify="center"
                            borderRadius="24px"
                            bg="white"
                            boxShadow="0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
                            flexShrink={0}
                            color="#48484A"
                          >
                            <HiChevronRight size={16} />
                          </Flex>
                        </HStack>
                      </HStack>
                    ) : (
                      /* Non-selected state */
                      <HStack
                        gap={2}
                        align="center"
                        px="18px"
                        py={2}
                        w="full"
                        cursor="pointer"
                        transition="all 200ms"
                        _hover={{ bg: 'surface.secondary' }}
                      >
                        <Box color="text.primary">
                          <Icon size={20} />
                        </Box>
                        <Text
                          fontSize="14px"
                          fontWeight="500"
                          color="text.primary"
                        >
                          {item.label}
                        </Text>
                      </HStack>
                    )}
                  </Link>
                </ChakraLink>
              </Box>
            );
          })}
        </VStack>

        {/* Bottom nav item (e.g., Settings) — divider above, mirrors AIM */}
        {bottomNavItem && (() => {
          const BottomIcon = bottomNavItem.icon;
          const isActive = location.pathname === bottomNavItem.path;
          return (
            <Box px="10px" pt="10px" borderTopWidth="1px" borderColor="#E5E5EA">
              <ChakraLink
                asChild
                display="block"
                w="full"
                _hover={{ textDecoration: 'none' }}
                _focusVisible={{
                  outline: '2px solid',
                  outlineColor: 'border.action',
                  outlineOffset: '2px',
                  borderRadius: '8px',
                }}
              >
                <Link
                  to={bottomNavItem.path}
                  style={{ textDecoration: 'none', display: 'block', width: '100%' }}
                >
                  <HStack
                    gap={2}
                    align="center"
                    px="8px"
                    py={2}
                    borderRadius="8px"
                    bg={isActive ? 'surface.menuSelect' : 'transparent'}
                    cursor="pointer"
                    transition="all 200ms"
                    _hover={{ bg: isActive ? 'surface.menuSelect' : 'surface.secondary' }}
                  >
                    <Box color={isActive ? 'text.action' : 'text.primary'}>
                      <BottomIcon size={20} />
                    </Box>
                    <Text fontSize="14px" fontWeight={isActive ? '600' : '500'} color="text.primary">
                      {bottomNavItem.label}
                    </Text>
                  </HStack>
                </Link>
              </ChakraLink>
            </Box>
          );
        })()}

        {/* Actions slot (e.g., color mode toggle) */}
        {actions && (
          <Box px="10px" pb="10px">
            {actions}
          </Box>
        )}

        {/* Current user dropdown at bottom */}
        {!hideUserMenu && currentUser && (
          <Box px="10px" pb="10px">
            {userMenuItems && userMenuItems.length > 0 ? (
              <Menu.Root>
                <Menu.Trigger asChild>
                  <HStack
                    as="button"
                    p={2}
                    borderRadius="8px"
                    borderWidth="1px"
                    borderColor="border.card"
                    bg="surface.searchInput"
                    cursor="pointer"
                    transition="all 200ms"
                    justifyContent="space-between"
                    w="100%"
                    _hover={{
                      bg: 'surface.input',
                      borderColor: 'border.card',
                    }}
                    _focusVisible={{
                      outline: '2px solid',
                      outlineColor: 'border.action',
                      outlineOffset: '2px',
                    }}
                    data-e2e="currentUser"
                  >
                    <HStack gap="10px">
                      {currentUser.avatar ? (
                        <Image
                          src={currentUser.avatar}
                          alt={currentUser.name}
                          borderRadius="full"
                          w="18px"
                          h="18px"
                          objectFit="cover"
                          data-e2e="user-avatar-image"
                        />
                      ) : (
                        <Box color="text.primary" data-e2e="user-avatar-icon">
                          <HiUser size={18} />
                        </Box>
                      )}
                      <Text
                        fontSize="12px"
                        fontWeight="500"
                        color="text.primary"
                        maxW="140px"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        textTransform="capitalize"
                        data-e2e="user-name-text"
                      >
                        {currentUser.name}
                      </Text>
                    </HStack>
                    <Box color="text.primary">
                      <HiChevronDown size={18} />
                    </Box>
                  </HStack>
                </Menu.Trigger>
                <Portal>
                  <Menu.Positioner>
                    <Menu.Content
                      zIndex="popover"
                      boxShadow="0px 2.5px 4px -1px rgba(0, 0, 0, 0.15)"
                      bg="surface.primary"
                      border="1px solid"
                      borderColor="border.secondary"
                      borderRadius="lg"
                      width="200px"
                    >
                      {userMenuItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <Menu.Item
                            key={item.label}
                            value={item.label}
                            textTransform="capitalize"
                            fontSize="16px"
                            fontWeight={500}
                            bg="surface.primary"
                            color="text.primary"
                            onClick={item.onClick}
                            borderRadius="none"
                            _hover={{
                              outline: 'none',
                              bg: 'surface.secondary',
                            }}
                            _focusVisible={{
                              outline: '2px solid',
                              outlineColor: 'border.action',
                              outlineOffset: '2px',
                            }}
                            data-e2e={item['data-e2e']}
                            gap={2}
                          >
                            {Icon && <Icon size={18} />}
                            {item.label}
                          </Menu.Item>
                        );
                      })}
                    </Menu.Content>
                  </Menu.Positioner>
                </Portal>
              </Menu.Root>
            ) : (
              <HStack
                p={2}
                borderRadius="8px"
                borderWidth="1px"
                borderColor="#C7C7CC"
                bg="#F2F2F7"
                cursor="pointer"
                transition="all 200ms"
                justifyContent="space-between"
                onClick={onUserMenuClick}
                _hover={{
                  bg: '#E5E5EA',
                }}
                data-e2e="user-menu-button"
              >
                <HStack gap="10px">
                  {currentUser.avatar ? (
                    <Image
                      src={currentUser.avatar}
                      alt={currentUser.name}
                      borderRadius="full"
                      w="18px"
                      h="18px"
                      objectFit="cover"
                      data-e2e="user-avatar-image"
                    />
                  ) : (
                    <Box color="#1C1C1E" data-e2e="user-avatar-icon">
                      <HiUser size={18} />
                    </Box>
                  )}
                  <Text fontSize="12px" fontWeight="500" color="#1C1C1E" data-e2e="user-name-text">
                    {currentUser.name}
                  </Text>
                </HStack>
                <Box color="#1C1C1E">
                  <HiChevronDown size={18} />
                </Box>
              </HStack>
            )}
          </Box>
        )}
      </Box>
    </>
  );
}
